import { Box, CardContent, Typography, Card, Stack, Avatar } from '@mui/material';

export interface StatusCardProps {
  icon: React.ComponentType<{ sx?: any }>;
  color: string;
  bgColor: string;
  label: string;
  description: string;
  count: number;
  /** The word under the count; "Request"/"Requests" by the count when unset. */
  countLabel?: string;
  secondaryMetric?: { value: string; label: string };
  onClick?: () => void;
}

const StatusCard: React.FC<StatusCardProps> = ({
  icon: Icon, color, bgColor, label, description, count, countLabel, secondaryMetric, onClick,
}) => {
  const isInteractive = !!onClick;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isInteractive && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick?.();
    }
  };

  return (
    <Card
      onClick={onClick}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      role={isInteractive ? 'button' : undefined}
      aria-label={`${label} — ${count} ${count === 1 ? 'item' : 'items'}`}
      sx={{
        height: '100%',
        background: 'linear-gradient(145deg, #ffffff 0%, #fafbfc 100%)',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #e1e7ef',
        cursor: isInteractive ? 'pointer' : 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${color} 0%, ${color}80 100%)`,
          opacity: 0.8,
        },
        ...(isInteractive && {
          '&:hover, &:focus': {
            transform: 'translateY(-4px) scale(1.02)',
            boxShadow: `0 8px 32px rgba(0,0,0,0.12), 0 0 20px ${color}20`,
            borderColor: color,
            outline: 'none',
            '&::before': { height: '6px', opacity: 1 },
          },
          '&:focus-visible': {
            outline: `3px solid ${color}80`,
            outlineOffset: '2px',
          },
        }),
      }}
    >
      <CardContent sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar sx={{ width: 20, height: 20, backgroundColor: bgColor, border: `1px solid ${color}30`, mr: 1 }}>
            <Icon sx={{ fontSize: 16, color }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.95rem', mb: 0.25, lineHeight: 1.2 }}>
              {label}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem', lineHeight: 1.2 }}>
              {description}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 'auto' }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color, fontSize: '1.35rem', lineHeight: 1, mb: 0.25 }}>
                {count}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>
                {countLabel ?? (count === 1 ? 'Request' : 'Requests')}
              </Typography>
            </Box>
            {secondaryMetric && (
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem', lineHeight: 1, mb: 0.25 }}>
                  {secondaryMetric.value}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>
                  {secondaryMetric.label}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        {/* Pulsing status indicator */}
        <Box sx={{
          position: 'absolute', top: 16, right: 16, width: 8, height: 8,
          borderRadius: '50%', backgroundColor: color,
          boxShadow: `0 0 8px ${color}60`,
          animation: count > 0 ? 'statusPulse 2s infinite' : 'none',
          '@keyframes statusPulse': {
            '0%': { opacity: 1, transform: 'scale(1)' },
            '50%': { opacity: 0.7, transform: 'scale(1.1)' },
            '100%': { opacity: 1, transform: 'scale(1)' },
          },
        }} />
      </CardContent>
    </Card>
  );
};

export default StatusCard;
